import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useEnhancedCompany } from '../../contexts/EnhancedCompanyContext';
import { collection, doc, getDocs, query, where } from '../../firebase.config';
import { writeBatch, addDoc, setGlobalCompanyId } from '../../utils/enforcedTracking';
import { db } from '../../firebase.config';
import { 
  readExcelFile, 
  getExcelSheets,
  generateAutoMappings, 
  validateRequiredFields, 
  parseNumericValue, 
  parseDateValue, 
  detectDateFormat,
  findDuplicates, 
  showToast,
  FIELD_DEFINITIONS 
} from './ImportUtils';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from '../shared/AppSelect';

const SalesImport = () => {
  const { currentCompany } = useEnhancedCompany();
  const companyId = currentCompany?.companyId || currentCompany?.id;
  
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileData, setFileData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [fieldMappings, setFieldMappings] = useState({});
  const [mappedData, setMappedData] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [existingInvoices, setExistingInvoices] = useState([]);
  const [availableSheets, setAvailableSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [showSheetSelection, setShowSheetSelection] = useState(false);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);
  
  const fileInputRef = useRef(null);
  const salesFields = FIELD_DEFINITIONS.sales;

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setFileData([]);
    setHeaders([]);
    setSelectedRows(new Set());
    setFieldMappings({});
    setMappedData([]);
    setImportResult(null);
    setAvailableSheets([]);
    setSelectedSheet(null);
    setShowSheetSelection(false);

    try {
      showToast.loading('Reading file...', { id: 'file-read' });
      
      // First, get available sheets
      const sheets = await getExcelSheets(file);
      setAvailableSheets(sheets);
      
      if (sheets.length === 1) {
        // Only one sheet, proceed directly
        await loadSheetData(file, sheets[0].name);
      } else {
        // Multiple sheets, show selection
        setShowSheetSelection(true);
        showToast.success(`Found ${sheets.length} sheets. Please select one to import.`, { id: 'file-read' });
      }
    } catch (error) {
      console.error('File read error:', error);
      showToast.error(`Failed to read file: ${error.message}`, { id: 'file-read' });
    } finally {
      // Dismiss file read loading toast
      showToast.dismiss('file-read');
    }
  };

  const loadSheetData = async (file, sheetName) => {
    try {
      showToast.loading(`Reading sheet "${sheetName}"...`, { id: 'sheet-read' });
      
      const result = await readExcelFile(file, sheetName);
      const { data, sheetName: actualSheetName } = result;
      
      if (!data || data.length === 0) {
        showToast.error('No data found in selected sheet', { id: 'sheet-read' });
        return;
      }
      
      // Debug: Log sample data to check for data type issues
      console.log('Sample data from Excel:', data.slice(0, 3));
      console.log('Data types in first row:', data[0] ? Object.keys(data[0]).map(key => ({ key, value: data[0][key], type: typeof data[0][key] })) : 'No data');
      
      // Debug: Check for date values specifically
      if (data[0]) {
        const dateFields = Object.keys(data[0]).filter(key => 
          key.toLowerCase().includes('date') || 
          key.toLowerCase().includes('time') ||
          key.toLowerCase().includes('day')
        );
        console.log('Date-related fields found:', dateFields);
        dateFields.forEach(field => {
          console.log(`Date field "${field}":`, data[0][field], 'Type:', typeof data[0][field]);
        });
      }

      setFileData(data);
      setSelectedSheet(actualSheetName);
      const headers = Object.keys(data[0]);
      setHeaders(headers);
      
      // Auto-select all rows by default
      setSelectedRows(new Set(data.map((_, index) => index)));
      
      // Auto-map common fields
      const autoMappings = generateAutoMappings(headers, salesFields);
      setFieldMappings(autoMappings);

      // Detect date format from sample data
      const dateColumn = autoMappings.date;
      if (dateColumn && data.length > 0) {
        const sampleDates = data.slice(0, 5).map(row => row[dateColumn]).filter(date => date);
        const dateFormat = detectDateFormat(sampleDates);
        if (dateFormat) {
          showToast.success(`Date format detected: ${dateFormat.format} (e.g., ${dateFormat.example})`, { 
            id: 'date-format',
            duration: 5000 
          });
        }
      }

      showToast.success(`Sheet "${actualSheetName}" loaded successfully! Found ${data.length} records.`, { id: 'sheet-read' });
      setCurrentStep(2);
      setShowSheetSelection(false);
    } catch (error) {
      console.error('Sheet read error:', error);
      showToast.error(`Failed to read sheet: ${error.message}`, { id: 'sheet-read' });
    } finally {
      // Dismiss sheet read loading toast
      showToast.dismiss('sheet-read');
    }
  };

  const handleSelectAll = () => {
    setSelectedRows(new Set(fileData.map((_, index) => index)));
  };

  const handleDeselectAll = () => {
    setSelectedRows(new Set());
  };

  const handleRowToggle = (index) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  // Fetch existing invoices for duplicate check
  const fetchExistingInvoices = async () => {
    if (!companyId) return [];
    
    try {
      const invoicesRef = collection(db, 'companies', companyId, 'salesBills');
      const snapshot = await getDocs(invoicesRef);
      const invoices = [];
      snapshot.forEach((doc) => {
        invoices.push({ id: doc.id, ...doc.data() });
      });
      return invoices;
    } catch (error) {
      console.error('Error fetching existing invoices:', error);
      return [];
    }
  };

  // Fetch existing parties
  const fetchExistingParties = async () => {
    if (!companyId) return [];
    
    try {
      const partiesRef = collection(db, 'companies', companyId, 'parties');
      const snapshot = await getDocs(partiesRef);
      const parties = [];
      snapshot.forEach((doc) => {
        parties.push({ id: doc.id, ...doc.data() });
      });
      return parties;
    } catch (error) {
      console.error('Error fetching existing parties:', error);
      return [];
    }
  };

  // Fetch existing items
  const fetchExistingItems = async () => {
    if (!companyId) return [];
    
    try {
      const itemsRef = collection(db, 'companies', companyId, 'items');
      const snapshot = await getDocs(itemsRef);
      const items = [];
      snapshot.forEach((doc) => {
        items.push({ id: doc.id, ...doc.data() });
      });
      return items;
    } catch (error) {
      console.error('Error fetching existing items:', error);
      return [];
    }
  };

  // Generate invoice number with sequence counter
  const generateInvoiceNumber = async (sequenceNumber = null) => {
    try {
      if (sequenceNumber !== null) {
        // Use provided sequence number
        const currentDate = new Date();
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        return `INV-${year}${month}-${String(sequenceNumber).padStart(4, '0')}`;
      }
      
      // Get current count from database
      const invoicesRef = collection(db, 'companies', companyId, 'salesBills');
      const q = query(invoicesRef);
      const snapshot = await getDocs(q);
      const count = snapshot.size;
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      return `INV-${year}${month}-${String(count + 1).padStart(4, '0')}`;
    } catch (error) {
      console.error('Error generating invoice number:', error);
      return `INV-${Date.now()}`;
    }
  };

  const generateMappedPreview = async () => {
    console.log('Starting preview generation...');
    console.log('Selected rows:', selectedRows.size);
    console.log('Field mappings:', fieldMappings);
    
    setIsGeneratingPreview(true);
    setPreviewProgress(0);
    
    try {
      const selectedData = fileData.filter((_, index) => selectedRows.has(index));
      console.log('Selected data length:', selectedData.length);
      setPreviewProgress(10);
      
      // Filter out rows with no data in required fields
      const filteredData = selectedData.filter(row => {
        const customerName = fieldMappings.customerName ? String(row[fieldMappings.customerName] || '').trim() : '';
        const area = fieldMappings.area ? parseNumericValue(row[fieldMappings.area], 0) : 0;
        const amount = fieldMappings.amount ? parseNumericValue(row[fieldMappings.amount], 0) : 0;
        
        // Skip rows with empty customer name, zero area, or zero amount
        return customerName !== '' && area > 0 && amount > 0;
      });
      
      console.log('Filtered data length:', filteredData.length);
      setPreviewProgress(20);
      
      if (filteredData.length === 0) {
        showToast.error('No valid data found. Please check that rows have customer name, area, and amount values.');
        return;
      }
      
      // Show filtering info
      const filteredCount = selectedData.length - filteredData.length;
      if (filteredCount > 0) {
        showToast.success(`Filtered out ${filteredCount} rows with missing data. Processing ${filteredData.length} valid rows.`, {
          duration: 4000
        });
      }
      
      setPreviewProgress(30);
      
      // Simple validation - just check if required fields are mapped
      console.log('Validating required fields...');
      const errors = [];
      
      // Check if required fields are mapped
      const requiredFields = salesFields.filter(field => field.required);
      const missingMappings = requiredFields.filter(field => !fieldMappings[field.key]);
      
      if (missingMappings.length > 0) {
        errors.push(`Missing required field mappings: ${missingMappings.map(f => f.label).join(', ')}`);
      }
      
      console.log('Field mappings:', fieldMappings);
      console.log('Missing mappings:', missingMappings);
      
      if (errors.length > 0) {
        showToast.error(`Please map the required fields: ${missingMappings.map(f => f.label).join(', ')}`);
        return;
      }
      
      setPreviewProgress(40);
      
      // Fetch existing data
      showToast.loading('Fetching existing data...', { id: 'preview-fetch' });
      const [existingInvoices, existingParties, existingItems] = await Promise.all([
        fetchExistingInvoices(),
        fetchExistingParties(),
        fetchExistingItems()
      ]);
      
      setPreviewProgress(60);
      setExistingInvoices(existingInvoices);
    
      setPreviewProgress(70);
      
      // Group data by invoice (same customer and date)
      showToast.loading('Grouping data by invoice...', { id: 'preview-group' });
      const groupedData = {};
      filteredData.forEach((row, index) => {
        const customerName = fieldMappings.customerName ? String(row[fieldMappings.customerName] || '').trim() : '';
        
        // Parse date using the fresh implementation
        const rawDate = fieldMappings.date ? row[fieldMappings.date] : null;
        const date = fieldMappings.date ? parseDateValue(rawDate) : new Date().toISOString();
        
        // Debug: Log date parsing for first few rows
        if (index < 3) {
          console.log(`Row ${index} date parsing:`, {
            rawDate: rawDate,
            rawDateType: typeof rawDate,
            parsedDate: date
          });
        }
        const key = `${customerName.toLowerCase().trim()}_${date}`;
        
        console.log(`Row ${index}: ${customerName} on ${date} -> Key: ${key}`);
        
        if (!groupedData[key]) {
          groupedData[key] = {
            customerName,
            date,
            items: [],
            invoiceNumber: fieldMappings.invoiceNumber ? String(row[fieldMappings.invoiceNumber] || '').trim() : '',
            dueDate: fieldMappings.dueDate ? parseDateValue(row[fieldMappings.dueDate]) : date,
            totalAmount: 0,
            taxAmount: 0,
            discount: 0,
            paymentStatus: 'Pending',
            notes: '',
            remarks: fieldMappings.notes ? String(row[fieldMappings.notes] || '').trim() : '',
            paidAmount: 0,
            outstandingAmount: 0
          };
        }
      
      // Create item entry with multi-dimensional measurements
      const workType = fieldMappings.workType ? String(row[fieldMappings.workType] || '').trim() : '';
      
      // Debug: Log raw values before parsing
      if (index < 5) { // Log first 5 rows for debugging
        console.log(`Row ${index} raw values:`, {
          nos: fieldMappings.nos ? row[fieldMappings.nos] : 'N/A',
          length: fieldMappings.length ? row[fieldMappings.length] : 'N/A',
          height: fieldMappings.height ? row[fieldMappings.height] : 'N/A',
          width: fieldMappings.width ? row[fieldMappings.width] : 'N/A',
          depth: fieldMappings.depth ? row[fieldMappings.depth] : 'N/A',
          area: fieldMappings.area ? row[fieldMappings.area] : 'N/A',
          rate: fieldMappings.rate ? row[fieldMappings.rate] : 'N/A',
          amount: fieldMappings.amount ? row[fieldMappings.amount] : 'N/A'
        });
        console.log(`Row ${index} field mappings:`, fieldMappings);
      }
      
      const nos = fieldMappings.nos ? parseNumericValue(row[fieldMappings.nos], 1) : 1;
      const length = fieldMappings.length ? parseNumericValue(row[fieldMappings.length], 0) : 0;
      const height = fieldMappings.height ? parseNumericValue(row[fieldMappings.height], 0) : 0;
      const width = fieldMappings.width ? parseNumericValue(row[fieldMappings.width], 0) : 0;
      const depth = fieldMappings.depth ? parseNumericValue(row[fieldMappings.depth], 0) : 0;
      const area = fieldMappings.area ? parseNumericValue(row[fieldMappings.area], 0) : 0;
      const rate = fieldMappings.rate ? parseNumericValue(row[fieldMappings.rate], 0) : 0;
      const amount = fieldMappings.amount ? parseNumericValue(row[fieldMappings.amount], 0) : 0;
      
      // Debug: Log parsed values
      if (index < 5) {
        console.log(`Row ${index} parsed values:`, {
          nos, length, height, width, depth, area, rate, amount
        });
      }
      
      // Build measurement string
      const measurements = [];
      if (length > 0) measurements.push(`L:${length}`);
      if (height > 0) measurements.push(`H:${height}`);
      if (width > 0) measurements.push(`W:${width}`);
      if (depth > 0) measurements.push(`D:${depth}`);
      const measurementString = measurements.length > 0 ? measurements.join(' x ') : '';
      
      const item = {
        itemName: workType || `Item ${index + 1}`,
        quantity: area || nos,
        rate: rate,
        amount: amount,
        gstRate: 0,
        gstAmount: 0,
        nos: nos,
        measurements: {
          length: length,
          height: height,
          width: width,
          depth: depth,
          area: area,
          measurementString: measurementString
        },
        // Check if party exists (case-insensitive)
        partyExists: existingParties.some(party => 
          party.firmName && party.firmName.toLowerCase().trim() === customerName.toLowerCase().trim()
        ),
        // Check if item exists (case-insensitive)
        itemExists: existingItems.some(item => 
          item.itemName && item.itemName.toLowerCase().trim() === workType.toLowerCase().trim()
        )
      };
      
      groupedData[key].items.push(item);
      groupedData[key].totalAmount += amount;
      
      // Determine payment status from remarks
      const remarks = fieldMappings.notes ? String(row[fieldMappings.notes] || '').trim() : '';
      if (remarks.toLowerCase() === 'pending') {
        groupedData[key].paymentStatus = 'Pending';
        groupedData[key].paidAmount = 0;
        groupedData[key].outstandingAmount = groupedData[key].totalAmount;
      } else if (remarks.toLowerCase() === 'paid' || remarks === '') {
        groupedData[key].paymentStatus = 'Paid';
        groupedData[key].paidAmount = groupedData[key].totalAmount;
        groupedData[key].outstandingAmount = 0;
      } else {
        // Parse payment details like "1500 Sid 220 Baki"
        const paymentMatch = remarks.match(/(\d+(?:\.\d+)?)\s+(\w+)\s+(\d+(?:\.\d+)?)\s+(\w+)/i);
        if (paymentMatch) {
          const paidAmount = parseFloat(paymentMatch[1]);
          const paidTo = paymentMatch[2];
          const outstandingAmount = parseFloat(paymentMatch[3]);
          const outstandingLabel = paymentMatch[4];
          
          groupedData[key].paymentStatus = 'Partial';
          groupedData[key].paidAmount = paidAmount;
          groupedData[key].outstandingAmount = outstandingAmount;
          groupedData[key].notes = `Paid: ₹${paidAmount} to ${paidTo}, Outstanding: ₹${outstandingAmount}`;
        } else {
          // If no specific payment info, assume pending
          groupedData[key].paymentStatus = 'Pending';
          groupedData[key].paidAmount = 0;
          groupedData[key].outstandingAmount = groupedData[key].totalAmount;
          groupedData[key].notes = remarks;
        }
      }
    });
    
    // Convert grouped data to invoice format
    console.log('Grouped data keys:', Object.keys(groupedData));
    console.log('Grouped data:', groupedData);
    
    const mapped = Object.values(groupedData).map((group, index) => {
      const invoice = {
        id: `temp_${index}`,
        invoiceNumber: group.invoiceNumber || '',
        customerName: group.customerName,
        date: group.date,
        dueDate: group.dueDate,
        totalAmount: group.totalAmount,
        taxAmount: group.taxAmount,
        discount: group.discount,
        paymentStatus: group.paymentStatus,
        paidAmount: group.paidAmount,
        outstandingAmount: group.outstandingAmount,
        notes: group.notes,
        items: group.items,
        isDuplicate: false,
        duplicateAction: 'skip',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        // Track what needs to be created
        needsPartyCreation: !group.items.some(item => item.partyExists),
        needsItemCreation: group.items.filter(item => !item.itemExists),
        remarks: group.remarks
      };
      
      console.log(`Invoice ${index}:`, invoice);
      return invoice;
    });

      setPreviewProgress(90);
      
      // Check for duplicates
      showToast.loading('Checking for duplicates...', { id: 'preview-duplicates' });
      const mappedWithDuplicates = findDuplicates(mapped, existingInvoices, 'invoiceNumber');
      
      setPreviewProgress(100);
      setMappedData(mappedWithDuplicates);
      
      showToast.success(`Preview generated successfully! ${mappedWithDuplicates.length} invoices ready to import.`, { 
        id: 'preview-complete' 
      });
      setCurrentStep(3);
      
    } catch (error) {
      console.error('Preview generation error:', error);
      showToast.error(`Failed to generate preview: ${error.message}`, { id: 'preview-error' });
    } finally {
      setIsGeneratingPreview(false);
      setPreviewProgress(0);
      // Dismiss all loading toasts
      showToast.dismiss('file-read');
      showToast.dismiss('sheet-read');
      showToast.dismiss('preview-fetch');
      showToast.dismiss('preview-group');
      showToast.dismiss('preview-duplicates');
    }
  };

  // Handle editing individual invoices
  const handleEditInvoice = (index) => {
    setEditingIndex(index);
  };

  const handleSaveEdit = (index, updatedInvoice) => {
    const newMappedData = [...mappedData];
    newMappedData[index] = { ...updatedInvoice, id: `temp_${index}` };
    setMappedData(newMappedData);
    setEditingIndex(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  // Handle duplicate action changes
  const handleDuplicateActionChange = (index, action) => {
    const newMappedData = [...mappedData];
    newMappedData[index].duplicateAction = action;
    setMappedData(newMappedData);
  };

  const handleImport = async () => {
    if (!companyId) {
      showToast.error('No company selected');
      return;
    }
    
    // 🚦 Enable automatic operation tracking for imports
    setGlobalCompanyId(companyId);

    if (!mappedData || mappedData.length === 0) {
      showToast.error('No data to import');
      return;
    }

    setIsImporting(true);
    try {
      showToast.loading('Importing invoices...', { id: 'import' });
      
      // Fetch existing data again for import
      const [existingInvoices, existingParties, existingItems] = await Promise.all([
        fetchExistingInvoices(),
        fetchExistingParties(),
        fetchExistingItems()
      ]);
      
      // Get current invoice count for sequential numbering
      const existingCount = existingInvoices.length;
      let sequenceCounter = existingCount + 1;
      
      const batch = writeBatch(db);
      const salesRef = collection(db, 'companies', companyId, 'salesBills');
      const partiesRef = collection(db, 'companies', companyId, 'parties');
      const itemsRef = collection(db, 'companies', companyId, 'items');
      
      let successCount = 0;
      let errorCount = 0;
      let partiesCreated = 0;
      let itemsCreated = 0;

      for (const invoice of mappedData) {
        try {
          // Handle duplicates
          if (invoice.isDuplicate && invoice.duplicateAction === 'skip') {
            console.log('Skipping duplicate invoice:', invoice.invoiceNumber);
            continue;
          }

          // Generate invoice number if not provided
          let invoiceNumber = invoice.invoiceNumber.trim();
          if (!invoiceNumber) {
            invoiceNumber = await generateInvoiceNumber(sequenceCounter);
            sequenceCounter++; // Increment for next invoice
            console.log(`Generated invoice number: ${invoiceNumber} (sequence: ${sequenceCounter - 1})`);
          }

          // Create party if needed (check for duplicates first)
          let partyId = null;
          if (invoice.needsPartyCreation) {
            // Check if party was already created in this batch
            const existingPartyInBatch = existingParties.find(party => 
              party.firmName && party.firmName.toLowerCase().trim() === invoice.customerName.toLowerCase().trim()
            );
            
            if (!existingPartyInBatch) {
              const partyData = {
                firmName: invoice.customerName.trim(),
                personName: '',
                contact: '',
                email: '',
                whatsapp: '',
                address: '',
                gstin: '',
                gstType: 'Unregistered', // Fixed: Default to Unregistered instead of Regular
                partyType: 'Buyer',
                openingBalance: 0, // TODO: Add opening balance field mapping if needed
                isActive: true,
                companyId: companyId,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              };
              const partyRef = doc(partiesRef);
              batch.set(partyRef, partyData);
              partyId = partyRef.id;
              partiesCreated++;
              
              // Add to existing parties to prevent duplicates in same batch
              existingParties.push({ id: partyId, ...partyData });
            } else {
              partyId = existingPartyInBatch.id;
            }
          }

          // Create items if needed (check for duplicates first)
          const processedItems = [];
          for (const item of invoice.items) {
            let itemId = null;
            if (!item.itemExists) {
              // Check if item was already created in this batch
              const existingItemInBatch = existingItems.find(existingItem => 
                existingItem.itemName && existingItem.itemName.toLowerCase().trim() === item.itemName.toLowerCase().trim()
              );
              
              if (!existingItemInBatch) {
                const itemData = {
                  itemName: item.itemName.trim(),
                  description: item.measurements.measurementString || '',
                  defaultRate: item.rate,
                  quantityMeasurement: 'pcs',
                  barcodeNumber: '',
                  hsnCode: '',
                  gstRate: 0,
                  category: 'Imported',
                  openingStock: 0,
                  currentStock: 0,
                  minStockLevel: 0,
                  maxStockLevel: 0,
                  itemType: 'product',
                  unit: 'pcs',
                  isActive: true,
                  companyId: companyId,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                };
                const itemRef = doc(itemsRef);
                batch.set(itemRef, itemData);
                itemId = itemRef.id;
                itemsCreated++;
                
                // Add to existing items to prevent duplicates in same batch
                existingItems.push({ id: itemId, ...itemData });
              } else {
                itemId = existingItemInBatch.id;
              }
            }

            // Process item for invoice
            const processedItem = {
              itemName: item.itemName,
              name: item.itemName, // Add name field for compatibility
              quantity: item.quantity,
              qty: item.quantity, // Add qty field for compatibility
              rate: item.rate,
              amount: item.amount,
              total: item.amount, // Add total field for compatibility
              gstRate: item.gstRate,
              gstAmount: item.gstAmount,
              nos: item.nos,
              measurements: item.measurements,
              item: itemId, // Add item field with item ID for Sales component compatibility
              itemId: itemId, // Keep itemId for backward compatibility
              description: item.measurements.measurementString || item.itemName // Add description for compatibility
            };
            processedItems.push(processedItem);
          }

          const invoiceData = {
            number: invoiceNumber, // Add number field for compatibility
            invoiceNumber: invoiceNumber,
            customerName: invoice.customerName.trim(),
            partyName: invoice.customerName.trim(), // Add partyName for payment compatibility
            party: partyId, // Add party field with party ID for Sales component compatibility
            partyId: partyId, // Add partyId field for compatibility
            date: invoice.date,
            invoiceDate: invoice.date, // Add invoiceDate for compatibility
            dueDate: invoice.dueDate || invoice.date,
            totalAmount: parseNumericValue(invoice.totalAmount, 0),
            amount: parseNumericValue(invoice.totalAmount, 0), // Add amount field
            grandTotal: parseNumericValue(invoice.totalAmount, 0), // Add grandTotal for payment compatibility
            taxAmount: parseNumericValue(invoice.taxAmount, 0),
            discount: parseNumericValue(invoice.discount, 0),
            paymentStatus: invoice.paymentStatus || 'Pending',
            paidAmount: parseNumericValue(invoice.paidAmount, 0),
            outstandingAmount: parseNumericValue(invoice.outstandingAmount, 0),
            // Add payment-related fields for compatibility
            paid: parseNumericValue(invoice.paidAmount, 0),
            outstanding: parseNumericValue(invoice.outstandingAmount, 0),
            notes: invoice.notes || '',
            items: processedItems,
            rows: processedItems, // Add rows field for compatibility
            isActive: true,
            companyId: companyId,
            userId: companyId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          console.log('Creating invoice:', invoiceData);

          if (invoice.isDuplicate && invoice.duplicateAction === 'update') {
            // Update existing invoice
            const docRef = doc(db, 'companies', companyId, 'sales', invoice.duplicateId);
            batch.update(docRef, invoiceData);
            successCount++;
          } else if (invoice.isDuplicate && invoice.duplicateAction === 'merge') {
            // Merge with existing invoice
            const existing = invoice.existingItem;
            const mergedData = {
              ...invoiceData,
              totalAmount: parseNumericValue(existing.totalAmount, 0) + parseNumericValue(invoice.totalAmount, 0),
              taxAmount: parseNumericValue(existing.taxAmount, 0) + parseNumericValue(invoice.taxAmount, 0),
              notes: invoice.notes || existing.notes || ''
            };
            const docRef = doc(db, 'companies', companyId, 'sales', invoice.duplicateId);
            batch.update(docRef, mergedData);
            successCount++;
          } else {
            // Create new invoice
            const docRef = doc(salesRef);
            batch.set(docRef, invoiceData);
            successCount++;
          }
        } catch (error) {
          console.error('Error preparing invoice:', error, invoice);
          errorCount++;
        }
      }

      console.log('Committing batch with', successCount, 'invoices...');
      await batch.commit();
      console.log('Batch committed successfully');
      
      setImportResult({
        success: successCount,
        errors: errorCount,
        total: mappedData.length,
        partiesCreated,
        itemsCreated
      });
      
      showToast.success(`Import completed! ${successCount} invoices, ${partiesCreated} parties, and ${itemsCreated} items created.`, { id: 'import' });
      setCurrentStep(4);
    } catch (error) {
      console.error('Import error:', error);
      showToast.error(`Import failed: ${error.message}`, { id: 'import' });
      setImportResult({
        success: 0,
        errors: mappedData.length,
        total: mappedData.length,
        partiesCreated: 0,
        itemsCreated: 0
      });
      setCurrentStep(4);
    } finally {
      setIsImporting(false);
      // Dismiss import loading toast
      showToast.dismiss('import');
    }
  };

  const resetImport = () => {
    setCurrentStep(1);
    setSelectedFile(null);
    setFileData([]);
    setHeaders([]);
    setSelectedRows(new Set());
    setFieldMappings({});
    setMappedData([]);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Import Sales Invoices from Excel</h3>
              <p className="text-gray-600 mb-8">Upload your Excel file to import sales invoices in bulk</p>
            </div>
            
            {!showSheetSelection ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                <div className="text-6xl text-green-500 mb-4">📄</div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Select Excel File</h4>
                <p className="text-gray-600 mb-6">Choose an Excel file (.xlsx, .xls) containing your sales data</p>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer text-lg font-medium"
                >
                  Choose Excel File
                </label>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Select Sheet to Import</h4>
                  <p className="text-gray-600 mb-6">Your Excel file contains multiple sheets. Please select which one to import:</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availableSheets.map((sheet, index) => (
                    <div
                      key={index}
                      onClick={() => loadSheetData(selectedFile, sheet.name)}
                      className="border border-gray-300 rounded-lg p-4 cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium text-gray-900">{sheet.name}</h5>
                        <div className="text-2xl">📊</div>
                      </div>
                      <p className="text-sm text-gray-600">{sheet.rowCount} rows of data</p>
                    </div>
                  ))}
                </div>
                
                <div className="text-center">
                  <button
                    onClick={() => {
                      setShowSheetSelection(false);
                      setAvailableSheets([]);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    ← Choose Different File
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Preview & Select Rows</h3>
                {selectedSheet && (
                  <p className="text-sm text-gray-600 mt-1">Sheet: <span className="font-medium">{selectedSheet}</span></p>
                )}
              </div>
              <div className="text-sm text-gray-600">
                {selectedRows.size} of {fileData.length} rows selected
              </div>
            </div>

            {/* Row Selection Controls */}
            <div className="flex space-x-4">
              <button
                onClick={handleSelectAll}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Select All
              </button>
              <button
                onClick={handleDeselectAll}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Deselect All
              </button>
            </div>

            {/* Data Preview Table */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-96">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700 w-12">
                        <input
                          type="checkbox"
                          checked={selectedRows.size === fileData.length}
                          onChange={(e) => e.target.checked ? handleSelectAll() : handleDeselectAll()}
                        />
                      </th>
                      {headers.map((header, index) => (
                        <th key={index} className="px-4 py-3 text-left font-medium text-gray-700">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fileData.map((row, index) => (
                      <tr key={index} className={`border-t ${selectedRows.has(index) ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(index)}
                            onChange={() => handleRowToggle(index)}
                          />
                        </td>
                        {headers.map((header, colIndex) => (
                          <td key={colIndex} className="px-4 py-3 text-gray-600">
                            {String(row[header] || '').substring(0, 50)}
                            {String(row[header] || '').length > 50 && '...'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Date Format Help */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">Date Format Support</h4>
                  <p className="text-sm text-blue-700 mb-2">
                    The system automatically detects and supports these date formats:
                  </p>
                  <div className="text-xs text-blue-600 space-y-1">
                    <div>• <strong>DD-MM-YYYY:</strong> 16-09-2024 (most common in Indian Excel)</div>
                    <div>• <strong>DD/MM/YYYY:</strong> 16/09/2024</div>
                    <div>• <strong>DD-MM-YY:</strong> 16-09-24</div>
                    <div>• <strong>DD/MM/YY:</strong> 16/09/24</div>
                    <div>• <strong>YYYY-MM-DD:</strong> 2024-09-16</div>
                    <div>• <strong>Excel Serial Numbers:</strong> 45123 (days since 1900-01-01)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Field Mapping Section */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Map Excel Columns to Invoice Fields</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {salesFields.map((field) => (
                  <div key={field.key} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <AppSelect
                      value={fieldMappings[field.key] || ''}
                      onChange={(e) => setFieldMappings({...fieldMappings, [field.key]: e.target.value})}
                      placeholder="Select Column"
                      aria-label={field.label}
                      sheetTitle={field.label}
                      options={headers.map(header => ({ value: header, label: header }))}
                      triggerClassName={`${APP_SELECT_FILTER_TRIGGER} focus:ring-green-500`}
                    />
                    <p className="text-xs text-gray-500">{field.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Progress Bar */}
            {isGeneratingPreview && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">Generating Preview...</span>
                  <span className="text-sm text-blue-600">{previewProgress}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${previewProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Validation Messages */}
            {selectedRows.size === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-yellow-800 text-sm">Please select at least one row to import.</p>
              </div>
            )}
            {selectedRows.size > 0 && !fieldMappings.customerName && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-sm">Please map the Customer/Party Name field.</p>
              </div>
            )}
            {selectedRows.size > 0 && !fieldMappings.workType && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-sm">Please map the Work Type/Item Name field.</p>
              </div>
            )}

            <div className="flex space-x-4">
              <button
                onClick={generateMappedPreview}
                disabled={selectedRows.size === 0 || !fieldMappings.customerName || !fieldMappings.workType || isGeneratingPreview}
                className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isGeneratingPreview ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  'Generate Preview'
                )}
              </button>
              <button
                onClick={() => setCurrentStep(1)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-900">Mapped Data Preview</h3>
              <div className="text-sm text-gray-600">
                {mappedData.length} invoices ready to import
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-green-800">
                <strong>Preview:</strong> Review the mapped data below. All selected rows will be imported as sales invoices.
              </div>
            </div>

            {/* Mapped Data Preview */}
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-96">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Invoice #</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Customer</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Date</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Items</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Measurements</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Amount</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Payment</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Auto-Create</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mappedData.map((invoice, index) => (
                      <tr key={index} className={`border-t hover:bg-gray-50 ${invoice.isDuplicate ? 'bg-yellow-50' : ''}`}>
                        <td className="px-4 py-3">
                          {invoice.isDuplicate ? (
                            <div className="flex flex-col space-y-1">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Duplicate
                              </span>
                              <AppSelect
                                value={invoice.duplicateAction}
                                onChange={(e) => handleDuplicateActionChange(index, e.target.value)}
                                aria-label="Duplicate action"
                                sheetTitle="Duplicate action"
                                options={[
                                  { value: 'skip', label: 'Skip' },
                                  { value: 'update', label: 'Update' },
                                  { value: 'merge', label: 'Merge' },
                                ]}
                                triggerClassName="text-xs border border-gray-300 rounded px-1 py-0.5 min-h-[28px] flex items-center justify-between gap-1 touch-manipulation w-full"
                              />
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              New
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-900 font-medium">
                          {invoice.invoiceNumber || 'Auto-generated'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{invoice.customerName}</td>
                        <td className="px-4 py-3 text-gray-600">{new Date(invoice.date).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-gray-600">
                          <div className="max-w-xs">
                            {invoice.items.map((item, idx) => (
                              <div key={idx} className="text-xs mb-1">
                                {item.itemName} (Qty: {item.quantity})
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          <div className="max-w-xs">
                            {invoice.items.map((item, idx) => (
                              <div key={idx} className="text-xs mb-1">
                                {item.measurements.measurementString || 'N/A'}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">₹{invoice.totalAmount}</td>
                        <td className="px-4 py-3 text-gray-600">
                          <div className="text-xs">
                            <div className="font-medium">{invoice.paymentStatus}</div>
                            {invoice.paidAmount > 0 && (
                              <div className="text-green-600">Paid: ₹{invoice.paidAmount}</div>
                            )}
                            {invoice.outstandingAmount > 0 && (
                              <div className="text-red-600">Outstanding: ₹{invoice.outstandingAmount}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          <div className="space-y-1">
                            {invoice.needsPartyCreation && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Party
                              </span>
                            )}
                            {invoice.needsItemCreation && invoice.needsItemCreation.length > 0 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                {invoice.needsItemCreation.length} Items
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleEditInvoice(index)}
                            className="text-green-600 hover:text-green-800 text-xs font-medium"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleImport}
                disabled={isImporting}
                className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
              >
                {isImporting ? 'Importing...' : `Import ${mappedData.length} Invoices`}
              </button>
              <button
                onClick={() => setCurrentStep(2)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
            </div>

            {/* Edit Modal */}
            {editingIndex !== null && (
              <EditInvoiceModal
                invoice={mappedData[editingIndex]}
                onSave={(updatedInvoice) => handleSaveEdit(editingIndex, updatedInvoice)}
                onCancel={handleCancelEdit}
              />
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="text-6xl text-green-500 mb-4">✅</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Import Completed Successfully!</h3>
            </div>

            {importResult && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{importResult.success}</div>
                    <div className="text-gray-600">Invoices Imported</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{importResult.partiesCreated || 0}</div>
                    <div className="text-gray-600">Parties Created</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600">{importResult.itemsCreated || 0}</div>
                    <div className="text-gray-600">Items Created</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-600">{importResult.errors}</div>
                    <div className="text-gray-600">Errors</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-600">{importResult.total}</div>
                    <div className="text-gray-600">Total Processed</div>
                  </div>
                </div>
              </div>
            )}

            <div className="text-center">
              <button
                onClick={resetImport}
                className="bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 font-medium"
              >
                Import More Invoices
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-6">
          <Link
            to="/import"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Import Selection
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                      currentStep >= step
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {currentStep > step ? '✓' : step}
                  </div>
                  {step < 4 && (
                    <div
                      className={`w-20 h-1 mx-2 ${
                        currentStep > step ? 'bg-green-600' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-sm text-gray-600">
              <span>Upload File</span>
              <span>Map Fields</span>
              <span>Preview</span>
              <span>Complete</span>
            </div>
          </div>

          {/* Step Content */}
          <div className="mb-8">
            {renderStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Invoice Modal Component
const EditInvoiceModal = ({ invoice, onSave, onCancel }) => {
  const [editedInvoice, setEditedInvoice] = useState({ ...invoice });

  const handleFieldChange = (field, value) => {
    setEditedInvoice(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    onSave(editedInvoice);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[92dvh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Edit Invoice</h3>
            <button
              onClick={onCancel}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 touch-manipulation" aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number *</label>
              <input
                type="text"
                value={editedInvoice.invoiceNumber}
                onChange={(e) => handleFieldChange('invoiceNumber', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
              <input
                type="text"
                value={editedInvoice.customerName}
                onChange={(e) => handleFieldChange('customerName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                value={editedInvoice.date ? new Date(editedInvoice.date).toISOString().split('T')[0] : ''}
                onChange={(e) => handleFieldChange('date', new Date(e.target.value).toISOString())}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
              <input
                type="date"
                value={editedInvoice.dueDate ? new Date(editedInvoice.dueDate).toISOString().split('T')[0] : ''}
                onChange={(e) => handleFieldChange('dueDate', new Date(e.target.value).toISOString())}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount *</label>
              <input
                type="number"
                value={editedInvoice.totalAmount}
                onChange={(e) => handleFieldChange('totalAmount', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax Amount</label>
              <input
                type="number"
                value={editedInvoice.taxAmount}
                onChange={(e) => handleFieldChange('taxAmount', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Discount</label>
              <input
                type="number"
                value={editedInvoice.discount}
                onChange={(e) => handleFieldChange('discount', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
              <AppSelect
                value={editedInvoice.paymentStatus}
                onChange={(e) => handleFieldChange('paymentStatus', e.target.value)}
                aria-label="Payment status"
                sheetTitle="Payment status"
                options={[
                  { value: 'Pending', label: 'Pending' },
                  { value: 'Paid', label: 'Paid' },
                  { value: 'Partial', label: 'Partial' },
                ]}
                triggerClassName={`${APP_SELECT_FILTER_TRIGGER} focus:ring-green-500`}
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={editedInvoice.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesImport;
